<template>
  <div id="closet-body">
    <div class="page-header">
      <div class="title">신체 정보</div>
      <div class="subtitle">내 치수를 기록해 옷 사이즈의 기준으로 삼고, 한국인 평균과 비교해 보세요</div>
    </div>
    <div class="body-layout">
      <!-- 좌측: 신체정보 폼 -->
      <div class="body-form-section">
        <div class="form-section">
          <div class="form-title">
            <span>신체 정보</span>
            <div class="form-title-right">
              <div class="unit-toggle">
                <button :class="{ active: !useInch }" @click="useInch = false">cm</button>
                <button :class="{ active: useInch }" @click="useInch = true">inch</button>
              </div>
              <label class="toggle-label">
                <input type="checkbox" v-model="bodyForm.isPublic" />
                <span>공개</span>
              </label>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group sm">
              <label>성별</label>
              <select v-model="bodyForm.gender">
                <option value="">선택</option>
                <option value="MALE">남성</option>
                <option value="FEMALE">여성</option>
              </select>
            </div>
            <div class="form-group sm">
              <label>키 ({{ ul('cm') }}) <span class="help-icon" :data-tooltip="MEASURE_HELP.height">?</span></label>
              <input type="number" :value="iv(bodyForm.height, 'cm')" @change="si('height', $event, 'cm')" placeholder="175.0" step="0.1" />
            </div>
            <div class="form-group sm">
              <label>체중 (kg) <span class="help-icon" :data-tooltip="MEASURE_HELP.weight">?</span></label>
              <input type="number" :value="bodyForm.weight" @change="si('weight', $event, 'kg')" placeholder="70.0" step="0.1" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group sm">
              <label>어깨 ({{ ul('cm') }}) <span class="help-icon" :data-tooltip="MEASURE_HELP.shoulderWidth">?</span></label>
              <input type="number" :value="iv(bodyForm.shoulderWidth, 'cm')" @change="si('shoulderWidth', $event, 'cm')" placeholder="45.0" step="0.1" />
            </div>
            <div class="form-group sm">
              <label>가슴둘레 ({{ ul('cm') }}) <span class="help-icon" :data-tooltip="MEASURE_HELP.chest">?</span></label>
              <input type="number" :value="iv(bodyForm.chest, 'cm')" @change="si('chest', $event, 'cm')" placeholder="95.0" step="0.1" />
            </div>
            <div class="form-group sm">
              <label>허리둘레 ({{ ul('cm') }}) <span class="help-icon" :data-tooltip="MEASURE_HELP.waist">?</span></label>
              <input type="number" :value="iv(bodyForm.waist, 'cm')" @change="si('waist', $event, 'cm')" placeholder="80.0" step="0.1" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group sm">
              <label>엉덩이둘레 ({{ ul('cm') }}) <span class="help-icon" :data-tooltip="MEASURE_HELP.hip">?</span></label>
              <input type="number" :value="iv(bodyForm.hip, 'cm')" @change="si('hip', $event, 'cm')" placeholder="97.0" step="0.1" />
            </div>
            <div class="form-group sm">
              <label>팔길이 ({{ ul('cm') }}) <span class="help-icon" :data-tooltip="MEASURE_HELP.armLength">?</span></label>
              <input type="number" :value="iv(bodyForm.armLength, 'cm')" @change="si('armLength', $event, 'cm')" placeholder="60.0" step="0.1" />
            </div>
            <div class="form-group sm">
              <label>허벅지둘레 ({{ ul('cm') }}) <span class="help-icon" :data-tooltip="MEASURE_HELP.thigh">?</span></label>
              <input type="number" :value="iv(bodyForm.thigh, 'cm')" @change="si('thigh', $event, 'cm')" placeholder="55.0" step="0.1" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group sm">
              <label>인심 ({{ ul('cm') }}) <span class="help-icon" :data-tooltip="MEASURE_HELP.legLength">?</span></label>
              <input type="number" :value="iv(bodyForm.legLength, 'cm')" @change="si('legLength', $event, 'cm')" placeholder="78.0" step="0.1" />
            </div>
            <div class="form-group sm">
              <label>밑위길이 ({{ ul('cm') }}) <span class="help-icon" :data-tooltip="MEASURE_HELP.rise">?</span></label>
              <input type="number" :value="iv(bodyForm.rise, 'cm')" @change="si('rise', $event, 'cm')" placeholder="26.5" step="0.1" />
            </div>
            <div class="form-group sm">
              <label>총장 ({{ ul('cm') }}) <span class="help-icon" :data-tooltip="MEASURE_HELP.totalLength">?</span></label>
              <input type="number" :value="iv(bodyForm.totalLength, 'cm')" @change="si('totalLength', $event, 'cm')" placeholder="104.0" step="0.1" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group sm">
              <label>발사이즈 ({{ ul('mm') }}) <span class="help-icon" :data-tooltip="MEASURE_HELP.footSize">?</span></label>
              <input type="number" :value="iv(bodyForm.footSize, 'mm')" @change="si('footSize', $event, 'mm')" placeholder="270" step="1" />
            </div>
          </div>
          <div class="form-actions">
            <button class="btn-primary" @click="saveBody">저장</button>
          </div>
        </div>
      </div>

      <!-- 우측: 신체 치수 다이어그램 -->
      <div class="body-svg-section">
        <div class="svg-card">
          <div class="svg-title">내 신체 치수</div>
          <div class="svg-container">
            <svg viewBox="0 0 360 470" xmlns="http://www.w3.org/2000/svg">
              <path d="
                M 141,68
                A 24,24 0 1 1 159,68
                L 159,78
                L 200,88
                L 270,158
                Q 278,168 252,176
                L 182,106
                L 182,222
                L 198,390
                L 216,390
                L 216,406
                L 172,406
                L 172,390
                L 162,234
                Q 150,244 138,234
                L 128,390
                L 128,406
                L 84,406
                L 84,390
                L 102,390
                L 118,222
                L 118,106
                L 48,176
                Q 22,168 30,158
                L 100,88
                L 141,78
                Z
              " fill="#e8edf3" stroke="#555" stroke-width="2.5" stroke-linejoin="round"/>

              <!-- ===== 치수선들 ===== -->

              <!-- 1. 키 -->
              <g v-if="bodyForm.height">
                <line x1="306" y1="22" x2="306" y2="406" stroke="#888" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line x1="302" y1="22" x2="310" y2="22" stroke="#888" stroke-width="1"/>
                <line x1="302" y1="406" x2="310" y2="406" stroke="#888" stroke-width="1"/>
                <line x1="174" y1="22" x2="306" y2="22" stroke="#888" stroke-width="0.4" stroke-dasharray="2,3" opacity="0.3"/>
                <rect x="282" y="200" width="66" height="24" rx="4" fill="#fff" stroke="#ddd" stroke-width="0.5"/>
                <text x="315" y="212" text-anchor="middle" class="label-text">{{ dv(bodyForm.height, 'cm') }}</text>
                <text v-if="bodyForm.gender" x="315" y="221" text-anchor="middle" class="label-avg">
                  평균 {{ dvRaw(avgData.height, 'cm') }}
                  <tspan :fill="diffFill(bodyForm.height, avgData.height)">{{ svgDiff(bodyForm.height, avgData.height, 'cm') }}</tspan>
                </text>
              </g>

              <!-- 2. 어깨너비 -->
              <g v-if="bodyForm.shoulderWidth">
                <line x1="100" y1="80" x2="200" y2="80" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line x1="100" y1="76" x2="100" y2="84" stroke="#e74c3c" stroke-width="1"/>
                <line x1="200" y1="76" x2="200" y2="84" stroke="#e74c3c" stroke-width="1"/>
                <rect x="108" y="60" width="84" height="24" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text x="150" y="72" text-anchor="middle" class="label-text">어깨 {{ dv(bodyForm.shoulderWidth, 'cm') }}</text>
                <text v-if="bodyForm.gender" x="150" y="81" text-anchor="middle" class="label-avg">
                  평균 {{ dvRaw(avgData.shoulderWidth, 'cm') }}
                  <tspan :fill="diffFill(bodyForm.shoulderWidth, avgData.shoulderWidth)">{{ svgDiff(bodyForm.shoulderWidth, avgData.shoulderWidth, 'cm') }}</tspan>
                </text>
              </g>

              <!-- 3. 가슴둘레 -->
              <g v-if="bodyForm.chest">
                <line x1="46" y1="130" x2="118" y2="130" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line x1="118" y1="126" x2="118" y2="134" stroke="#e74c3c" stroke-width="1"/>
                <line x1="46" y1="126" x2="46" y2="134" stroke="#e74c3c" stroke-width="1"/>
                <rect x="2" y="116" width="90" height="24" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text x="47" y="128" text-anchor="middle" class="label-text">가슴 {{ dv(bodyForm.chest, 'cm') }}</text>
                <text v-if="bodyForm.gender" x="47" y="137" text-anchor="middle" class="label-avg">
                  평균 {{ dvRaw(avgData.chest, 'cm') }}
                  <tspan :fill="diffFill(bodyForm.chest, avgData.chest)">{{ svgDiff(bodyForm.chest, avgData.chest, 'cm') }}</tspan>
                </text>
              </g>

              <!-- 4. 허리둘레 -->
              <g v-if="bodyForm.waist">
                <line x1="46" y1="165" x2="118" y2="165" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line x1="118" y1="161" x2="118" y2="169" stroke="#e74c3c" stroke-width="1"/>
                <line x1="46" y1="161" x2="46" y2="169" stroke="#e74c3c" stroke-width="1"/>
                <rect x="2" y="150" width="90" height="24" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text x="47" y="162" text-anchor="middle" class="label-text">허리 {{ dv(bodyForm.waist, 'cm') }}</text>
                <text v-if="bodyForm.gender" x="47" y="171" text-anchor="middle" class="label-avg">
                  평균 {{ dvRaw(avgData.waist, 'cm') }}
                  <tspan :fill="diffFill(bodyForm.waist, avgData.waist)">{{ svgDiff(bodyForm.waist, avgData.waist, 'cm') }}</tspan>
                </text>
              </g>

              <!-- 5. 엉덩이둘레 -->
              <g v-if="bodyForm.hip">
                <line x1="38" y1="210" x2="118" y2="210" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line x1="118" y1="206" x2="118" y2="214" stroke="#e74c3c" stroke-width="1"/>
                <line x1="38" y1="206" x2="38" y2="214" stroke="#e74c3c" stroke-width="1"/>
                <rect x="2" y="196" width="90" height="24" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text x="47" y="208" text-anchor="middle" class="label-text">엉덩이 {{ dv(bodyForm.hip, 'cm') }}</text>
                <text v-if="bodyForm.gender" x="47" y="217" text-anchor="middle" class="label-avg">
                  평균 {{ dvRaw(avgData.hip, 'cm') }}
                  <tspan :fill="diffFill(bodyForm.hip, avgData.hip)">{{ svgDiff(bodyForm.hip, avgData.hip, 'cm') }}</tspan>
                </text>
              </g>

              <!-- 6. 팔길이 -->
              <g v-if="bodyForm.armLength">
                <line x1="258" y1="88" x2="258" y2="158" stroke="#3498db" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line x1="254" y1="88" x2="262" y2="88" stroke="#3498db" stroke-width="1"/>
                <line x1="254" y1="158" x2="262" y2="158" stroke="#3498db" stroke-width="1"/>
                <line x1="200" y1="88" x2="258" y2="88" stroke="#3498db" stroke-width="0.4" stroke-dasharray="2,3" opacity="0.3"/>
                <line x1="270" y1="158" x2="258" y2="158" stroke="#3498db" stroke-width="0.4" stroke-dasharray="2,3" opacity="0.3"/>
                <rect x="234" y="112" width="66" height="24" rx="3" fill="#fff" stroke="#c0d8f0" stroke-width="0.5"/>
                <text x="267" y="124" text-anchor="middle" class="label-text label-blue">팔 {{ dv(bodyForm.armLength, 'cm') }}</text>
                <text v-if="bodyForm.gender" x="267" y="133" text-anchor="middle" class="label-avg">
                  평균 {{ dvRaw(avgData.armLength, 'cm') }}
                  <tspan :fill="diffFill(bodyForm.armLength, avgData.armLength)">{{ svgDiff(bodyForm.armLength, avgData.armLength, 'cm') }}</tspan>
                </text>
              </g>

              <!-- 7. 허벅지둘레 -->
              <g v-if="bodyForm.thigh">
                <line x1="32" y1="310" x2="108" y2="310" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line x1="108" y1="306" x2="108" y2="314" stroke="#e74c3c" stroke-width="1"/>
                <line x1="32" y1="306" x2="32" y2="314" stroke="#e74c3c" stroke-width="1"/>
                <rect x="2" y="298" width="90" height="24" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text x="47" y="310" text-anchor="middle" class="label-text">허벅지 {{ dv(bodyForm.thigh, 'cm') }}</text>
                <text v-if="bodyForm.gender" x="47" y="319" text-anchor="middle" class="label-avg">
                  평균 {{ dvRaw(avgData.thigh, 'cm') }}
                  <tspan :fill="diffFill(bodyForm.thigh, avgData.thigh)">{{ svgDiff(bodyForm.thigh, avgData.thigh, 'cm') }}</tspan>
                </text>
              </g>

              <!-- 8. 인심 -->
              <g v-if="bodyForm.legLength">
                <line x1="228" y1="234" x2="228" y2="390" stroke="#3498db" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line x1="224" y1="234" x2="232" y2="234" stroke="#3498db" stroke-width="1"/>
                <line x1="224" y1="390" x2="232" y2="390" stroke="#3498db" stroke-width="1"/>
                <line x1="162" y1="234" x2="228" y2="234" stroke="#3498db" stroke-width="0.4" stroke-dasharray="2,3" opacity="0.3"/>
                <rect x="206" y="304" width="66" height="24" rx="3" fill="#fff" stroke="#c0d8f0" stroke-width="0.5"/>
                <text x="239" y="316" text-anchor="middle" class="label-text label-blue">인심 {{ dv(bodyForm.legLength, 'cm') }}</text>
                <text v-if="bodyForm.gender" x="239" y="325" text-anchor="middle" class="label-avg">
                  평균 {{ dvRaw(avgData.legLength, 'cm') }}
                  <tspan :fill="diffFill(bodyForm.legLength, avgData.legLength)">{{ svgDiff(bodyForm.legLength, avgData.legLength, 'cm') }}</tspan>
                </text>
              </g>

              <!-- 9. 밑위길이 -->
              <g v-if="bodyForm.rise">
                <line x1="248" y1="165" x2="248" y2="234" stroke="#27ae60" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line x1="244" y1="165" x2="252" y2="165" stroke="#27ae60" stroke-width="1"/>
                <line x1="244" y1="234" x2="252" y2="234" stroke="#27ae60" stroke-width="1"/>
                <line x1="182" y1="165" x2="248" y2="165" stroke="#27ae60" stroke-width="0.4" stroke-dasharray="2,3" opacity="0.3"/>
                <rect x="218" y="186" width="66" height="24" rx="3" fill="#fff" stroke="#b0d8b0" stroke-width="0.5"/>
                <text x="251" y="198" text-anchor="middle" class="label-text label-green">밑위 {{ dv(bodyForm.rise, 'cm') }}</text>
                <text v-if="bodyForm.gender" x="251" y="207" text-anchor="middle" class="label-avg">
                  평균 {{ dvRaw(avgData.rise, 'cm') }}
                  <tspan :fill="diffFill(bodyForm.rise, avgData.rise)">{{ svgDiff(bodyForm.rise, avgData.rise, 'cm') }}</tspan>
                </text>
              </g>

              <!-- 10. 총장 -->
              <g v-if="bodyForm.totalLength">
                <line x1="338" y1="165" x2="338" y2="390" stroke="#27ae60" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line x1="334" y1="165" x2="342" y2="165" stroke="#27ae60" stroke-width="1"/>
                <line x1="334" y1="390" x2="342" y2="390" stroke="#27ae60" stroke-width="1"/>
                <line x1="182" y1="165" x2="338" y2="165" stroke="#27ae60" stroke-width="0.4" stroke-dasharray="2,3" opacity="0.3"/>
                <rect x="314" y="268" width="50" height="24" rx="3" fill="#fff" stroke="#b0d8b0" stroke-width="0.5"/>
                <text x="339" y="280" text-anchor="middle" class="label-text label-green">총장 {{ dv(bodyForm.totalLength, 'cm') }}</text>
                <text v-if="bodyForm.gender" x="339" y="289" text-anchor="middle" class="label-avg">
                  평균 {{ dvRaw(avgData.totalLength, 'cm') }}
                  <tspan :fill="diffFill(bodyForm.totalLength, avgData.totalLength)">{{ svgDiff(bodyForm.totalLength, avgData.totalLength, 'cm') }}</tspan>
                </text>
              </g>

              <!-- 11. 발사이즈 -->
              <g v-if="bodyForm.footSize">
                <rect x="110" y="416" width="80" height="24" rx="3" fill="#fff" stroke="#ddd" stroke-width="0.5"/>
                <text x="150" y="428" text-anchor="middle" class="label-text">발 {{ dv(bodyForm.footSize, 'mm') }}</text>
                <text v-if="bodyForm.gender" x="150" y="437" text-anchor="middle" class="label-avg">
                  평균 {{ dvRaw(avgData.footSize, 'mm') }}
                  <tspan :fill="diffFill(bodyForm.footSize, avgData.footSize)">{{ svgDiff(bodyForm.footSize, avgData.footSize, 'mm') }}</tspan>
                </text>
              </g>

              <!-- 12. 체중 -->
              <g v-if="bodyForm.weight">
                <rect x="112" y="446" width="76" height="24" rx="4" fill="#f0f6ff" stroke="#d6e4f0" stroke-width="0.5"/>
                <text x="150" y="458" text-anchor="middle" class="label-text-lg">{{ bodyForm.weight }}kg</text>
                <text v-if="bodyForm.gender" x="150" y="467" text-anchor="middle" class="label-avg">
                  평균 {{ avgData.weight }}kg
                  <tspan :fill="diffFill(bodyForm.weight, avgData.weight)">{{ svgDiff(bodyForm.weight, avgData.weight, 'kg') }}</tspan>
                </text>
              </g>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- 히스토리 리스트 -->
    <div class="history-section">
      <div class="history-card">
        <div class="history-title">신체정보 히스토리 ({{ historyList.length }}건)</div>
        <template v-if="historyList.length > 0">
          <!-- 테이블 (PC) -->
          <div class="history-table-wrap">
            <table class="history-table">
              <thead>
                <tr>
                  <th>측정일</th>
                  <th>키</th>
                  <th>체중</th>
                  <th>어깨</th>
                  <th>가슴</th>
                  <th>허리</th>
                  <th>엉덩이</th>
                  <th>허벅지</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in historyList" :key="item.id">
                  <td>{{ fmtDate(item.measuredDate) }}</td>
                  <td>{{ item.height ?? '-' }}</td>
                  <td>{{ item.weight ?? '-' }}</td>
                  <td>{{ item.shoulderWidth ?? '-' }}</td>
                  <td>{{ item.chest ?? '-' }}</td>
                  <td>{{ item.waist ?? '-' }}</td>
                  <td>{{ item.hip ?? '-' }}</td>
                  <td>{{ item.thigh ?? '-' }}</td>
                  <td>
                    <button class="btn-delete" @click="deleteHistory(item.id)">삭제</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- 카드 (모바일) -->
          <div class="history-cards">
            <div class="history-card-item" v-for="item in historyList" :key="'m-' + item.id">
              <div class="card-header">
                <span class="card-date">{{ fmtDate(item.measuredDate) }}</span>
                <button class="btn-delete" @click="deleteHistory(item.id)">삭제</button>
              </div>
              <div class="card-body">
                <span v-if="item.height">키 {{ item.height }}</span>
                <span v-if="item.weight">체중 {{ item.weight }}</span>
                <span v-if="item.shoulderWidth">어깨 {{ item.shoulderWidth }}</span>
                <span v-if="item.chest">가슴 {{ item.chest }}</span>
                <span v-if="item.waist">허리 {{ item.waist }}</span>
                <span v-if="item.hip">엉덩이 {{ item.hip }}</span>
                <span v-if="item.thigh">허벅지 {{ item.thigh }}</span>
                <span v-if="item.footSize">발 {{ item.footSize }}mm</span>
              </div>
            </div>
          </div>
        </template>
        <div v-else class="history-empty">
          신체정보를 저장하면 히스토리가 쌓입니다.
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, onMounted } from "vue";
import { KOREAN_AVG_BODY, ClosetBodyHistory } from "../class";
import * as closetApi from "../api";

const MEASURE_HELP: Record<string, string> = {
  height: "신발을 벗고 똑바로 선 상태에서 바닥부터 머리 꼭대기까지의 높이입니다. 벽에 등을 붙이고 책을 머리 위에 올려 표시하면 정확하게 잴 수 있어요.",
  weight: "가벼운 실내복을 입은 상태에서 측정합니다. 아침 공복 시 측정하면 가장 정확해요.",
  shoulderWidth: "양쪽 어깨뼈 끝(견봉점)을 연결한 직선 거리입니다. 줄자를 목 뒤로 넘겨 어깨 끝에서 끝까지 재세요. 셔츠·재킷 사이즈 선택에 중요합니다.",
  chest: "가슴의 가장 넓은 부분(유두 높이)에 줄자를 수평으로 감아 한 바퀴 잰 둘레입니다. 숨을 편하게 쉰 상태에서 측정하세요.",
  waist: "배꼽 바로 위 가장 잘록한 부분(자연 허리선)에 줄자를 수평으로 감아 잰 둘레입니다. 바지를 고를 때 허리 사이즈 기준이 됩니다.",
  hip: "엉덩이의 가장 돌출된 부분에 줄자를 수평으로 감아 잰 둘레입니다. 바지·스커트의 힙 사이즈 기준이 됩니다.",
  armLength: "어깨뼈 끝(견봉)에서 팔을 자연스럽게 내린 상태로 손목뼈 끝까지의 길이입니다. 셔츠 소매 길이를 결정할 때 참고하세요.",
  legLength: "가랑이 안쪽 시작점(밑위)에서 발바닥까지의 길이로, 바지 인심(inseam) 기준입니다. 다리 안쪽을 따라 줄자를 대고 측정하세요.",
  rise: "허리 벨트라인(자연 허리선)에서 가랑이(밑위) 시작점까지의 수직 거리입니다. 바지를 입었을 때 허리부터 가랑이까지의 길이로, 총장에서 인심을 빼면 밑위길이가 됩니다.",
  totalLength: "허리 벨트라인에서 발바닥까지의 바깥쪽 전체 길이입니다. 밑위길이 + 인심 = 총장이 됩니다. 바지 총장 사이즈를 선택할 때 기준이 됩니다.",
  thigh: "허벅지 가장 굵은 부분(보통 가랑이 바로 아래)에 줄자를 수평으로 감아 잰 둘레입니다. 바지 핏 선택에 참고됩니다.",
  footSize: "발뒤꿈치 끝에서 가장 긴 발가락 끝까지 직선 거리(mm)입니다. 종이 위에 발을 올려놓고 앞뒤 끝을 표시하면 쉽게 잴 수 있어요.",
};

export default defineComponent({
  name: "ClosetBodyView",

  setup() {
    const useInch = ref(false);
    const historyList = ref<ClosetBodyHistory[]>([]);

    const bodyForm = reactive({
      gender: "",
      height: null as number | null,
      weight: null as number | null,
      shoulderWidth: null as number | null,
      chest: null as number | null,
      waist: null as number | null,
      hip: null as number | null,
      armLength: null as number | null,
      legLength: null as number | null,
      rise: null as number | null,
      totalLength: null as number | null,
      thigh: null as number | null,
      footSize: null as number | null,
      isPublic: false,
    });

    /** 폼 단위 라벨: cm→inch, mm→inch, kg→kg */
    const ul = (unit: string): string => {
      if (!useInch.value) return unit;
      if (unit === "kg") return "kg";
      return "inch";
    };

    /** 폼 input 표시값 (cm→inch 변환) */
    const iv = (value: number | null, unit: string): number | string => {
      if (value == null) return "";
      if (!useInch.value || unit === "kg") return value;
      if (unit === "mm") return +(value / 25.4).toFixed(1);
      return +(value / 2.54).toFixed(1);
    };

    /** 폼 input 저장 (inch→cm 역변환) */
    const si = (field: string, event: Event, unit: string) => {
      const raw = (event.target as HTMLInputElement).value;
      if (raw === "") {
        (bodyForm as any)[field] = null;
        return;
      }
      const num = parseFloat(raw);
      if (isNaN(num)) return;
      if (!useInch.value || unit === "kg") {
        (bodyForm as any)[field] = num;
        return;
      }
      if (unit === "mm") {
        (bodyForm as any)[field] = Math.round(num * 25.4);
        return;
      }
      (bodyForm as any)[field] = +(num * 2.54).toFixed(1);
    };

    /** SVG 라벨 표시값: "95.0cm" or "37.4in" */
    const dv = (value: number | null, unit: string): string => {
      if (value == null) return "-";
      if (!useInch.value) return unit === "mm" ? `${value}mm` : `${value}cm`;
      if (unit === "mm") return `${+(value / 25.4).toFixed(1)}in`;
      return `${+(value / 2.54).toFixed(1)}in`;
    };

    /** SVG 평균값 숫자만 (단위 없이) */
    const dvRaw = (value: number, unit: string): string => {
      if (!useInch.value) return String(value);
      if (unit === "mm") return String(+(value / 25.4).toFixed(1));
      return String(+(value / 2.54).toFixed(1));
    };

    /** SVG 차이 텍스트: "(+5.5)" or "(-2.1)" */
    const svgDiff = (mine: number | null, avg: number, unit: string): string => {
      if (mine == null) return "";
      let d: number;
      if (useInch.value && unit !== "kg") {
        const factor = unit === "mm" ? 25.4 : 2.54;
        d = +((mine - avg) / factor).toFixed(1);
      } else {
        d = +(mine - avg).toFixed(1);
      }
      if (d > 0) return `(+${d})`;
      if (d < 0) return `(${d})`;
      return "(0)";
    };

    /** 차이 색상: +오렌지 / -파랑 / 0초록 */
    const diffFill = (mine: number | null, avg: number): string => {
      if (mine == null) return "#999";
      const d = mine - avg;
      if (d > 0.05) return "#e65100";
      if (d < -0.05) return "#1565c0";
      return "#4caf50";
    };

    const fmtDate = (v: any): string => {
      if (!v) return "-";
      if (Array.isArray(v)) {
        return `${v[0]}.${String(v[1]).padStart(2, "0")}.${String(v[2]).padStart(2, "0")}`;
      }
      const p = String(v).split(/[-,./]/).map((s) => s.trim()).filter(Boolean);
      if (p.length < 3) return String(v);
      return `${p[0]}.${String(p[1]).padStart(2, "0")}.${String(p[2]).padStart(2, "0")}`;
    };

    const avgData = computed(() => {
      const gender = bodyForm.gender === "FEMALE" ? "FEMALE" : "MALE";
      return KOREAN_AVG_BODY[gender];
    });

    const fetchBody = async () => {
      const data = await closetApi.getMyBody();
      if (data) {
        bodyForm.gender = data.gender || "";
        bodyForm.height = data.height;
        bodyForm.weight = data.weight;
        bodyForm.shoulderWidth = data.shoulderWidth;
        bodyForm.chest = data.chest;
        bodyForm.waist = data.waist;
        bodyForm.hip = data.hip;
        bodyForm.armLength = data.armLength;
        bodyForm.legLength = data.legLength;
        bodyForm.rise = data.rise;
        bodyForm.totalLength = data.totalLength;
        bodyForm.thigh = data.thigh;
        bodyForm.footSize = data.footSize;
        bodyForm.isPublic = data.isPublic || false;
      }
    };

    const fetchHistoryList = async () => {
      const data = await closetApi.getBodyHistoryList();
      historyList.value = data || [];
    };

    const saveBody = async () => {
      const data = await closetApi.saveBody(bodyForm);
      if (data) {
        alert("신체정보가 저장되었습니다.");
        fetchHistoryList();
      }
    };

    const deleteHistory = async (id: number) => {
      if (!confirm("이 히스토리를 삭제하시겠습니까?")) return;
      await closetApi.deleteBodyHistory(id);
      fetchHistoryList();
    };

    onMounted(() => {
      fetchBody();
      fetchHistoryList();
    });

    return {
      bodyForm,
      useInch,
      ul,
      iv,
      si,
      dv,
      dvRaw,
      svgDiff,
      diffFill,
      avgData,
      saveBody,
      historyList,
      deleteHistory,
      fmtDate,
      MEASURE_HELP,
    };
  },
});
</script>

<style lang="scss">
@import "../assets/css/closet-body.scss";
</style>
